import React, { useCallback, useMemo, useRef } from 'react'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { IconUsers } from '../../../../../shared/icon/icon'
import { Menu, MenuAdd, MenuExport, MenuRecycle, MenuTableSettingToggle } from '../../../../../shared/menu/menu'
import { useLoginMenuItems } from './common/login-menu-items'
import { CardFullHeightScrollY } from '../../../../../shared/card/card-full-height-scroll-y'
import { useApiListLogin } from '../../../../../api-new/state/content'
import { useRoleSysLogin } from '../../../../../role/hook/role-sys'
import { ColumnText, ColumnDate, ColumnMenu, ColumnYesNo, ColumnSelector } from '../../../../../shared/table-builder/table-columns'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import moment from 'moment'
import { BadgeSecondary, BadgeSuccess, BadgeWarning } from '../../../../../shared/component/badge'
import { SECTION } from '../../../../../shared/navigation/sections'

const BadgeStatus = ({ userLogin }) => {
	let value = useMemo(() => {
		if (userLogin.lastActive) {
			let now = moment()
			let lastActive = moment(userLogin.lastActive)
			let minutes = now.diff(lastActive, 'minutes')

			if (minutes < 10) {
				return <BadgeSuccess value='Online' />
			} else if (minutes < 30) {
				return <BadgeWarning value='Away' />
			} else {
				return <BadgeSecondary value='Offline' />
			}
		}

		return <BadgeSecondary value='Offline' />
	}, [userLogin])

	return <React.Fragment>{value}</React.Fragment>
}

export function LoginList() {
	const apiListLogin = useApiListLogin()
	const roleSysLogin = useRoleSysLogin()
	// the same items as the login page's menu, plus Edit
	const loginMenuItems = useLoginMenuItems()

	const tableRef = useRef()
	const handleExportCsv = () => tableRef.current.exportCsv('logins')
	const handleTableSettingToggle = () => tableRef.current.toggleSideBar()

	const handleReady = async () => await apiListLogin.get()
	const handleDestroyed = () => apiListLogin.init()

	const HeaderMenu = () => (
		<Menu>
			<MenuAdd to='/admin/login/list/add' show={roleSysLogin.accessCreate} />
			<MenuExport onClick={handleExportCsv} show />
			<MenuTableSettingToggle onClick={handleTableSettingToggle} show />
			<MenuRecycle to='/admin/login/list/recycle' show={roleSysLogin.accessDelete} />
		</Menu>
	)

	const comparatorStatus = useCallback((value1, value2, node1, node2) => {
		if (node1.data.invitePending || node2.data.invitePending) {
			if (node2.data.invitePending) {
				return 1
			} else {
				return -1
			}
		}

		if (!node1.data.lastActive && !node2.data.lastActive) {
			return -1
		}

		let date1 = new Date(node1.data.lalastActivestAuth)
		let date2 = new Date(node2.data.lastActive)

		if (date1 > date2) {
			return 1
		} else {
			return -1
		}
	}, [])

	return (
		<React.Fragment>
			<BreadcrumbView Icon={SECTION.logins.Icon} text={SECTION.logins.text}>
				<CardFullHeightScrollY title='login' HeaderMenu={HeaderMenu}>
					<TableAgClient
						ref={tableRef}
						apiList={apiListLogin}
						onReady={handleReady}
						onDestoryed={handleDestroyed}
						emptyState={{
							Icon: IconUsers,
							title: 'No logins yet',
							message: 'Accounts appear here once people are added or sign up.',
							action: { label: 'Add login', to: '/admin/login/list/add', show: roleSysLogin.accessCreate }
						}}>
						<ColumnText field='email' />
						<ColumnText field='firstName' />
						<ColumnText field='lastName' />
						<ColumnDate field='createdAt' />

						<ColumnSelector field='lastActive' header='Status' selector={(row) => <BadgeStatus userLogin={row} />} comparator={comparatorStatus} />

						<ColumnText field='organizationName' header='Organization' />

						<ColumnSelector
							field='lastActive'
							header='Last Active'
							selector={(row) => (row.lastActive ? `${moment(row.lastActive).fromNow(true)} ago` : 'Never')}
						/>

						<ColumnYesNo field='isSysAdmin' header='Sys Admin' hide />
						<ColumnYesNo field='isSysRoot' header='Sys Root' hide />
						<ColumnMenu
							menu={(row) => (
								<Menu small>{loginMenuItems(row.id, { edit: true })}</Menu>
							)}
						/>
					</TableAgClient>
				</CardFullHeightScrollY>
			</BreadcrumbView>
		</React.Fragment>
	)
}
